import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, MapPin, Package, Wrench, Clock, Euro, Sparkles, Send,
  FileText, Loader2, Download, Copy, CheckCheck, User, Building2,
  MessageSquare, XCircle
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import {
  Card, Chip, Button, PageHeader, CompatibilityRing, MatchScoreBreakdown, LivePulse
} from "../components/Primitives";
import {
  getRequestByRef, getRequestAttachments, attachmentSignedUrl,
  proposalsForRequest, findChatForRequest, updateProposalStatus,
  completeRequest, archiveRequest, reopenRequest,
} from "../lib/db";

function fmtBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function RequestDetail() {
  const { ref: refParam } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [req, setReq] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [proposals, setProposals] = useState([]);
  const [proposalsLoading, setProposalsLoading] = useState(false);
  const [openingChatId, setOpeningChatId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getRequestByRef(refParam);
      setReq(r);
      if (r?.dbId) {
        try {
          const att = await getRequestAttachments(r.dbId);
          setAttachments(att);
        } catch (e) { console.warn("attachments read blocked", e); }
      }
    } catch (e) {
      console.error("request load failed", e);
      toast.error(e?.message || "Could not load request");
    } finally { setLoading(false); }
  }, [refParam]);

  useEffect(() => { load(); }, [load]);

  const isOwner = req && user?.id && req.ownerId === user.id;

  useEffect(() => {
    if (!isOwner || !req?.dbId) return;
    setProposalsLoading(true);
    proposalsForRequest(req.dbId).then((rows) => setProposals(rows)).finally(() => setProposalsLoading(false));
  }, [isOwner, req?.dbId]);

  const changeStatus = async (fn, successKey) => {
    if (!isOwner || !req?.dbId) return;
    try {
      await fn(req.dbId);
      toast.success(t(successKey));
      await load();
    } catch (e) { toast.error(e?.message || "Update failed"); }
  };
  const doComplete = () => changeStatus(completeRequest, "requestDetail.markedCompleted");
  const doArchive  = () => changeStatus(archiveRequest,  "requestDetail.archived");
  const doReopen   = () => changeStatus(reopenRequest,   "requestDetail.reopened");

  const copyRef = async () => {
    const value = req.id;
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        ok = true;
      }
    } catch { /* fall through */ }
    if (!ok) {
      try {
        const el = document.createElement("textarea");
        el.value = value; el.setAttribute("readonly", ""); el.style.position = "fixed"; el.style.top = "-9999px";
        document.body.appendChild(el); el.select();
        ok = document.execCommand("copy");
        document.body.removeChild(el);
      } catch { /* ignore */ }
    }
    if (ok) toast.success(t("requestDetail.copied"), { description: value });
    else toast.info(value, { description: t("requestDetail.copied") });
  };

  const openAttachment = async (path) => {
    const url = await attachmentSignedUrl(path);
    if (url) window.open(url, "_blank");
    else toast.error("Could not open attachment");
  };

  const openChatForProposal = async (applicantId) => {
    if (!req?.dbId || !user?.id || !applicantId) return;
    setOpeningChatId(applicantId);
    try {
      const { createOrGetChat } = await import("../lib/db");
      // Look for existing chat between owner (me) and THIS applicant on THIS request
      const existing = await findChatForRequest(user.id, req.dbId, applicantId);
      if (existing) { navigate(`/app/messages?chat=${existing}`); return; }
      const cid = await createOrGetChat(user.id, applicantId, req.dbId);
      navigate(`/app/messages?chat=${cid}`);
    } catch (e) {
      toast.error(e?.message || "Could not open chat");
    } finally { setOpeningChatId(null); }
  };

  const setStatus = async (proposalId, status) => {
    try {
      await updateProposalStatus(proposalId, status);
      setProposals((prev) => prev.map((p) => (p.id === proposalId ? { ...p, status } : p)));
      toast.success(status === "accepted" ? t("requestDetail.accepted") : t("requestDetail.declined"));
    } catch (e) { toast.error(e?.message || "Update failed"); }
  };

  if (loading) {
    return (
      <div className="p-8 max-w-4xl mx-auto animate-fade-up">
        <div className="text-ink-500 font-mono text-xs">Loading request…</div>
      </div>
    );
  }

  if (!req) {
    return (
      <div className="p-8 max-w-4xl mx-auto animate-fade-up">
        <button onClick={() => navigate(-1)} data-testid="request-back" className="text-ink-600 flex items-center gap-1 mb-4 hover:text-ink-900">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <Card className="p-10 text-center">
          <p className="text-ink-700 text-sm">{t("requestDetail.notFound")}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto animate-fade-up" data-testid="request-detail-page">
      <button onClick={() => navigate(-1)} data-testid="request-back" className="text-ink-600 flex items-center gap-1 mb-4 hover:text-ink-900 text-sm">
        <ArrowLeft className="w-4 h-4" /> {t("common.back")}
      </button>

      <PageHeader
        kicker={<>R4Pro · Request{isOwner ? " · Manage" : ""}</>}
        title={lang === "it" ? req.title : req.titleEn}
        testId="request-detail-header"
        right={
          <div className="flex flex-wrap gap-2 items-center">
            {isOwner && (
              <Chip className="!bg-electric-500/10 !text-electric-600 !border-electric-500/40 !text-[10px]" data-testid="owner-badge">MANAGE</Chip>
            )}
            <button onClick={copyRef} data-testid="copy-request-id" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-carbon-600 text-xs text-ink-800 hover:border-electric-500 hover:text-electric-600 transition font-mono">
              <Copy className="w-3.5 h-3.5" /> {req.id}
            </button>
            {req.isNew && <LivePulse label="NEW" />}
            {req.urgent && (
              <Chip className="!bg-red-500/10 !text-red-300 !border-red-500/30 !text-[10px]">
                {t("requests.urgent")}
              </Chip>
            )}
            <Chip data-testid="request-status-chip">{req.status?.toUpperCase() || "OPEN"}</Chip>
            {isOwner && (
              <div className="flex gap-2 ml-1" data-testid="lifecycle-actions">
                {(req.status === "open" || req.status === "draft" || req.status === "closed") && (
                  <button onClick={doComplete} data-testid="mark-completed-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/10 text-xs transition">
                    <CheckCheck className="w-3.5 h-3.5" /> {t("requestDetail.markCompleted")}
                  </button>
                )}
                {req.status !== "archived" && (
                  <button onClick={doArchive} data-testid="archive-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-carbon-600 text-ink-700 hover:border-ink-500 text-xs transition">
                    <XCircle className="w-3.5 h-3.5" /> {t("requestDetail.archive")}
                  </button>
                )}
                {req.status === "archived" && (
                  <button onClick={doReopen} data-testid="reopen-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-electric-500/40 text-electric-600 hover:bg-electric-500/10 text-xs transition">
                    {t("requestDetail.reopen")}
                  </button>
                )}
              </div>
            )}
          </div>
        }
      />

      <div className="flex items-center gap-3 text-xs text-ink-600 mb-4 flex-wrap">
        <span className="flex items-center gap-1">
          <User className="w-3 h-3" /> {req.client}
        </span>
        <span>·</span>
        <span className="font-mono">{req.postedAt}</span>
        {req.location && (
          <>
            <span>·</span>
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {req.location}</span>
          </>
        )}
      </div>

      <Card className="p-5 md:p-6 mb-5" data-testid="request-summary-card">
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryStat icon={Package} label={t("newRequest.material")} value={req.material || "—"} />
          <SummaryStat icon={Wrench} label={t("newRequest.category")} value={req.category || "—"} />
          <SummaryStat icon={FileText} label={t("requests.qty")} value={req.quantity ? String(req.quantity) : "—"} mono />
          <SummaryStat icon={Euro} label={t("newRequest.budget")} value={req.budget} mono />
          <SummaryStat icon={Clock} label={t("newRequest.deadline")} value={req.deadline || "—"} />
          <SummaryStat icon={MapPin} label={t("newRequest.location")} value={req.location || "—"} />
        </div>

        {req.tags?.length > 0 && (
          <div className="mt-5 pt-5 border-t border-carbon-600 flex flex-wrap gap-1.5">
            {req.tags.map((tag) => <Chip key={tag}>{tag}</Chip>)}
          </div>
        )}
      </Card>

      {req.description && (
        <Card className="p-5 md:p-6 mb-5" data-testid="request-description-card">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-electric-600" />
            <span className="text-[11px] font-mono uppercase tracking-widest text-ink-600">
              {t("newRequest.description")}
            </span>
          </div>
          <p className="text-sm text-ink-800 leading-relaxed whitespace-pre-wrap">{req.description}</p>
        </Card>
      )}

      {attachments.length > 0 && (
        <Card className="p-5 mb-5" data-testid="request-attachments-card">
          <div className="text-[11px] font-mono uppercase tracking-widest text-ink-600 mb-3 flex items-center gap-2">
            <FileText className="w-3.5 h-3.5" /> {t("newRequest.files")} · {attachments.length}
          </div>
          <ul className="divide-y divide-carbon-600">
            {attachments.map((a) => (
              <li key={a.id} className="py-2.5 flex items-center gap-3">
                <FileText className="w-4 h-4 text-ink-500 shrink-0" />
                <span className="text-sm text-ink-800 truncate flex-1" title={a.filename}>{a.filename}</span>
                <span className="text-[11px] font-mono text-ink-500">{fmtBytes(a.sizeBytes)}</span>
                <button onClick={() => openAttachment(a.path)} data-testid={`open-attachment-${a.id}`} className="inline-flex items-center gap-1 text-electric-600 hover:text-electric-500 text-xs">
                  <Download className="w-3.5 h-3.5" /> {t("requestDetail.download")}
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card className="p-5 mb-5" data-testid="request-compat-card">
        <div className="flex items-start gap-4">
          <CompatibilityRing score={req.compatibility} size={72} stroke={5} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-3.5 h-3.5 text-electric-600" />
              <span className="text-[11px] font-mono uppercase tracking-widest text-electric-600">
                {t("requestDetail.compatibility")}
              </span>
            </div>
            <p className="text-xs text-ink-700 leading-snug">
              {t("requestDetail.compatibilityHelp")}
            </p>
            <div className="mt-3">
              <MatchScoreBreakdown
                reasons={req.matchReasons.map((rr) => ({
                  score: rr.score,
                  label: t(`matchScore.${rr.label.toLowerCase()}`),
                }))}
              />
            </div>
          </div>
        </div>
      </Card>

      {isOwner ? (
        <Card className="p-5 mb-5" data-testid="request-proposals-card">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-ink-900 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-electric-600" />
              {t("requestDetail.proposals")} · {proposals.length}
            </div>
          </div>
          {proposalsLoading ? (
            <div className="text-ink-500 text-xs">Loading proposals…</div>
          ) : proposals.length === 0 ? (
            <p className="text-xs text-ink-600 py-4">{t("requestDetail.noProposals")}</p>
          ) : (
            <ul className="divide-y divide-carbon-600">
              {proposals.map((p) => (
                <li key={p.id} className="py-4 cursor-pointer hover:bg-carbon-800/30 rounded-lg -mx-2 px-2 transition"
                    onClick={() => navigate(`/app/proposals/${p.id}`)}
                    data-testid={`proposal-${p.id}`}>
                  <div className="flex items-start gap-3">
                    <button type="button" className="w-9 h-9 rounded-lg bg-carbon-750 border border-carbon-600 flex items-center justify-center font-mono text-xs text-ink-800 shrink-0 cursor-pointer hover:border-electric-500 transition"
                      onClick={() => p.applicant?.id && navigate(`/app/producers/${p.applicant.id}`)}
                      data-testid={`view-producer-${p.id}`}
                      aria-label={`Open profile of ${p.applicant?.name || "producer"}`}
                    >
                      {(p.applicant?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <button onClick={() => p.applicant?.id && navigate(`/app/producers/${p.applicant.id}`)}
                            className="text-sm font-medium text-ink-900 truncate hover:text-electric-600 transition"
                            data-testid={`producer-link-${p.id}`}
                          >
                            {p.applicant?.name || "—"}
                          </button>
                          {p.applicant?.accountType === "ORGANIZATION" ? (
                            <Building2 className="w-3 h-3 text-ink-500" />
                          ) : (
                            <User className="w-3 h-3 text-ink-500" />
                          )}
                          <Chip className={
                            p.status === "accepted" ? "!bg-status-available/10 !text-status-available !border-status-available/30" :
                            p.status === "declined" ? "!bg-red-500/10 !text-red-400 !border-red-500/30" : ""
                          }>{p.status}</Chip>
                        </div>
                        <span className="text-[10px] font-mono text-ink-600 shrink-0">{p.when}</span>
                      </div>
                      {p.excerpt && <p className="text-xs text-ink-700 mt-1.5 leading-snug whitespace-pre-wrap">{p.excerpt}</p>}
                      <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-ink-600">
                        {p.proposedPrice != null && <span className="font-mono">{t("proposal.price")}: € {Number(p.proposedPrice).toLocaleString("it-IT")}</span>}
                        {p.proposedDays != null && <span className="font-mono">{t("proposal.days")}: {p.proposedDays}</span>}
                      </div>
                      {p.status === "pending" && (
                        <div className="mt-3 flex gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                          <Button className="!py-1.5 !px-3 !text-xs" onClick={() => navigate(`/app/proposals/${p.id}`)} data-testid={`open-proposal-${p.id}`}>
                            <MessageSquare className="w-3.5 h-3.5" /> {t("requestDetail.openProposal")}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : req.status === "open" ? (
        <div className="sticky bottom-4 md:static md:bottom-auto">
          <Button onClick={() => navigate(`/app/requests/${req.id}/reply`)} data-testid="request-reply-cta" className="w-full h-12 text-base">
            <Send className="w-4 h-4" />
            {t("requestDetail.replyCta")}
          </Button>
          <p className="text-[11px] text-ink-500 text-center mt-2">{t("requestDetail.replyHelp")}</p>
        </div>
      ) : (
        <Card className="p-6 text-center" data-testid="request-not-open-block">
          <p className="text-ink-900 text-sm font-medium">{t("requestDetail.notOpenTitle")}</p>
          <p className="text-ink-600 text-xs mt-1">{t("requestDetail.notOpenHint")}</p>
        </Card>
      )}
    </div>
  );
}

function SummaryStat({ icon: Icon, label, value, mono = false }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-ink-600 mb-1 flex items-center gap-1.5">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`text-sm text-ink-900 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
